import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

const PreWeddingEvents = () => {
    // Get URL parameters
    const urlParams = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return {
            haldi: params.get('haldi') === 'true',
            mehendi: params.get('mehendi') === 'true',
            sangeet: params.get('sangeet') === 'true'
        };
    }, []);

    // Check if ANY event parameter is set in URL
    const hasEventParams = urlParams.haldi || urlParams.mehendi || urlParams.sangeet;

    // ⚠️ IF NO URL PARAMS, DON'T SHOW THE SECTION AT ALL
    if (!hasEventParams) {
        return null;
    }

    const events = [
        {
            id: 1,
            name: 'Haldi Ceremony',
            hindi: 'हल्दी रस्म',
            date: 'February 04, 2026',
            time: '11:00 AM',
            venue: 'Amaatra Suits',
            address: 'Sector 5, Vasundhara, Ghaziabad',
            description: 'Join us for a vibrant morning filled with turmeric, laughter, and blessings as we celebrate the traditional Haldi ceremony.',
            icon: '🌼',
            theme: 'haldi',
            colorFrom: 'from-yellow-400',
            colorTo: 'to-pink-400',
            bgGradient: 'from-yellow-50 via-pink-50 to-orange-50',
            borderColor: 'border-yellow-400',
            accentColor: 'text-yellow-600',
            buttonBg: 'bg-gradient-to-r from-yellow-500 to-pink-500',
            buttonHover: 'hover:from-yellow-600 hover:to-pink-600',
            decoration: '✨',
            visible: urlParams.haldi  // Only show if URL has haldi=true
        },
        {
            id: 2,
            name: 'Mehendi Ceremony',
            hindi: 'मेहंदी रस्म',
            date: 'February 04, 2026',
            time: '7:00 PM',
            venue: 'Amaatra Suits',
            address: 'Sector 5, Vasundhara, Ghaziabad',
            description: 'Celebrate with us as intricate henna designs adorn our hands, accompanied by music, dance, and joyful celebrations.',
            icon: '🌿',
            theme: 'mehendi',
            colorFrom: 'from-green-400',
            colorTo: 'to-emerald-600',
            bgGradient: 'from-green-50 via-emerald-50 to-teal-50',
            borderColor: 'border-green-500',
            accentColor: 'text-green-700',
            buttonBg: 'bg-gradient-to-r from-green-500 to-emerald-600',
            buttonHover: 'hover:from-green-600 hover:to-emerald-700',
            decoration: '🍃',
            visible: urlParams.haldi
        }
    ];

    // Filter only visible events
    const visibleEvents = events.filter(event => event.visible);

    // If no events to show, don't render anything
    if (visibleEvents.length === 0) {
        return null;
    }

    return (
        <section className="py-32 bg-white relative overflow-hidden">
            {/* Decorative Background Elements */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-20 left-10 w-40 h-40 bg-yellow-400 rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-40 h-40 bg-green-400 rounded-full blur-3xl"></div>
            </div>

            <div className="container mx-auto px-4 max-w-7xl relative z-10">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-20"
                >
                    <motion.div
                        className="inline-block mb-6"
                        animate={{ rotate: [0, 360] }}
                        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    >
                        <svg width="50" height="50" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="30" fill="none" stroke="#FCD34D" strokeWidth="1" />
                            <circle cx="50" cy="50" r="20" fill="none" stroke="#10B981" strokeWidth="1" />
                            <circle cx="50" cy="50" r="10" fill="none" stroke="#F472B6" strokeWidth="1" />
                        </svg>
                    </motion.div>

                    <h2 className="text-4xl md:text-6xl font-serif text-wedding-accent mb-4">
                        Pre-Wedding Celebrations
                    </h2>
                    <div className="flex items-center justify-center gap-3 mb-6">
                        <div className="w-16 h-[1px] bg-gradient-to-r from-transparent to-yellow-400"></div>
                        <span className="text-2xl">🌸</span>
                        <div className="w-2 h-2 bg-wedding-primary rounded-full"></div>
                        <span className="text-2xl">🌿</span>
                        <div className="w-16 h-[1px] bg-gradient-to-l from-transparent to-green-500"></div>
                    </div>
                    <p className="text-wedding-charcoal/60 text-lg">
                        Join us for the colorful festivities before the big day
                    </p>
                </motion.div>

                {/* Events Grid - Dynamic columns based on number of events */}
                <div className={`grid gap-8 lg:gap-12 max-w-6xl mx-auto ${visibleEvents.length === 1 ? 'md:grid-cols-1 max-w-2xl' :
                    visibleEvents.length === 2 ? 'md:grid-cols-2' :
                        'md:grid-cols-2 lg:grid-cols-3'
                    }`}>
                    {visibleEvents.map((event, index) => (
                        <motion.div
                            key={event.id}
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: index * 0.2 }}
                            className="relative group"
                        >
                            {/* 3D Card Effect */}
                            <div className="relative">
                                {/* Shadow Layer */}
                                <div className={`absolute inset-0 bg-gradient-to-br ${event.colorFrom} ${event.colorTo} opacity-20 transform translate-x-3 translate-y-3 transition-transform group-hover:translate-x-4 group-hover:translate-y-4`}></div>

                                {/* Main Card */}
                                <div className={`relative bg-gradient-to-br ${event.bgGradient} border-4 ${event.borderColor} overflow-hidden`}>
                                    {/* Decorative Pattern Overlay */}
                                    <div className="absolute inset-0 opacity-10">
                                        <div className="absolute inset-0" style={{
                                            backgroundImage: event.theme === 'haldi'
                                                ? `radial-gradient(circle, #FCD34D 1px, transparent 1px)`
                                                : event.theme === 'mehendi'
                                                    ? `radial-gradient(circle, #10B981 1px, transparent 1px)`
                                                    : `radial-gradient(circle, #A855F7 1px, transparent 1px)`,
                                            backgroundSize: '30px 30px'
                                        }}></div>
                                    </div>

                                    {/* Corner Decorations */}
                                    <div className="absolute top-0 left-0 text-6xl opacity-20">
                                        {event.decoration}
                                    </div>
                                    <div className="absolute bottom-0 right-0 text-6xl opacity-20 transform rotate-180">
                                        {event.decoration}
                                    </div>

                                    {/* Content */}
                                    <div className="relative p-8 md:p-10">
                                        {/* Icon */}
                                        <motion.div
                                            className="text-7xl mb-4 text-center"
                                            animate={{
                                                rotate: [0, 10, -10, 0],
                                                scale: [1, 1.1, 1]
                                            }}
                                            transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                                        >
                                            {event.icon}
                                        </motion.div>

                                        {/* Event Name */}
                                        <h3 className={`text-3xl md:text-4xl font-serif ${event.accentColor} font-bold text-center mb-2`}>
                                            {event.name}
                                        </h3>
                                        <p className="text-center text-gray-600 italic mb-6 text-sm">
                                            {event.hindi}
                                        </p>

                                        {/* Divider */}
                                        <div className="flex items-center justify-center gap-2 mb-6">
                                            <div className={`w-12 h-[2px] bg-gradient-to-r ${event.colorFrom} ${event.colorTo}`}></div>
                                            <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${event.colorFrom} ${event.colorTo}`}></div>
                                            <div className={`w-12 h-[2px] bg-gradient-to-l ${event.colorFrom} ${event.colorTo}`}></div>
                                        </div>

                                        {/* Event Details */}
                                        <div className="space-y-4 mb-6">
                                            {/* Date */}
                                            <div className="flex items-start gap-3">
                                                <div className="flex-shrink-0 w-10 h-10 bg-white/60 rounded-full flex items-center justify-center">
                                                    <svg className={`w-5 h-5 ${event.accentColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <p className="text-sm uppercase tracking-wider text-gray-600 font-medium">Date</p>
                                                    <p className={`text-lg font-semibold ${event.accentColor}`}>{event.date}</p>
                                                </div>
                                            </div>

                                            {/* Time */}
                                            <div className="flex items-start gap-3">
                                                <div className="flex-shrink-0 w-10 h-10 bg-white/60 rounded-full flex items-center justify-center">
                                                    <svg className={`w-5 h-5 ${event.accentColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <p className="text-sm uppercase tracking-wider text-gray-600 font-medium">Time</p>
                                                    <p className={`text-lg font-semibold ${event.accentColor}`}>{event.time}</p>
                                                </div>
                                            </div>

                                            {/* Venue */}
                                            <div className="flex items-start gap-3">
                                                <div className="flex-shrink-0 w-10 h-10 bg-white/60 rounded-full flex items-center justify-center">
                                                    <svg className={`w-5 h-5 ${event.accentColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <p className="text-sm uppercase tracking-wider text-gray-600 font-medium">Venue</p>
                                                    <p className={`text-lg font-semibold ${event.accentColor}`}>{event.venue}</p>
                                                    <p className="text-sm text-gray-600">{event.address}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Description */}
                                        <div className="bg-white/60 backdrop-blur-sm p-4 rounded-lg mb-6">
                                            <p className="text-gray-700 text-sm leading-relaxed">
                                                {event.description}
                                            </p>
                                        </div>

                                        {/* Dress Code */}
                                        <div className="bg-white/80 p-4 rounded-lg mb-6 text-center">
                                            <p className="text-xs uppercase tracking-wider text-gray-600 font-medium mb-1">
                                                Dress Code
                                            </p>
                                            <p className={`text-lg font-semibold ${event.accentColor}`}>
                                                {event.theme === 'haldi' ? 'Shades of Pink Attire 💖' :
                                                    event.theme === 'mehendi' ? 'Green Traditional Wear 💚' :
                                                        'Purple & Pink Festive Attire 💜💖'}
                                            </p>
                                        </div>

                                        {/* CTA Button */}
                                        <motion.a
                                            href={`https://maps.app.goo.gl/mG6imbpe9XN31KLH7?g_st=ic`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`w-full ${event.buttonBg} ${event.buttonHover} text-white py-4 text-sm uppercase tracking-widest font-semibold transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2`}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            Get Directions
                                        </motion.a>

                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Bottom Note */}
                <motion.div
                    className="mt-16 text-center"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 }}
                >
                    <div className="inline-block bg-gradient-to-r from-yellow-100 via-pink-100 to-green-100 border-2 border-wedding-primary/30 px-8 py-4 rounded-full">
                        <p className="text-wedding-accent font-semibold flex items-center gap-2">
                            <span className="text-2xl">🎊</span>
                            <span>Your presence will make these moments special</span>
                            <span className="text-2xl">🎊</span>
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default PreWeddingEvents;
